import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';

import { LarsExportService } from '../services/lars-export.service';
import { ClearLarsExportLinks, LoadLarsExportLinks } from './lars-export.actions';
import { finalize, Observable, of, tap } from 'rxjs';
import { LarsExportResponse } from '@monorepo/interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';

export interface LarsExportStateModel {
    larsExportLinks: LarsExportResponse | undefined;
    isLoading: boolean;
    errorMessage: string | undefined;
}

@State<LarsExportStateModel>({
    name: 'larsExport',
    defaults: {
        larsExportLinks: undefined,
        isLoading: false,
        errorMessage: undefined
    }
})
@Injectable()
export class LarsExportState {
    private larsExportService = inject(LarsExportService);

    @Selector()
    static larsExportLinks(state: LarsExportStateModel): LarsExportResponse | undefined {
        return state.larsExportLinks;
    }

    @Selector()
    static isLoading(state: LarsExportStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static errorMessage(state: LarsExportStateModel): string | undefined {
        return state.errorMessage;
    }

    @Action(LoadLarsExportLinks)
    loadLarsExportLinks(
        ctx: StateContext<LarsExportStateModel>,
        action: LoadLarsExportLinks
    ): Observable<LarsExportResponse | HttpErrorResponse> {
        ctx.patchState({ isLoading: true, errorMessage: undefined });

        return this.larsExportService.getLarsExportLinks(action.payload.accountingUnitIds, action.payload.period).pipe(
            tap(larsExportLinks => {
                ctx.patchState({
                    larsExportLinks,
                    errorMessage: undefined
                });
            }),
            catchError(error => {
                ctx.patchState({
                    larsExportLinks: undefined,
                    errorMessage: error.error?.message || 'An error occurred'
                });
                return of(error);
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(ClearLarsExportLinks)
    clearLarsExportLinks(ctx: StateContext<LarsExportStateModel>): void {
        ctx.patchState({
            larsExportLinks: undefined,
            errorMessage: undefined
        });
    }

    private setIsLoading(ctx: StateContext<LarsExportStateModel>, isLoading: boolean): void {
        ctx.patchState({ isLoading: isLoading });
    }
}
